import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Nav from "../components/Nav";
import MatrixGuideOverlay from "../components/MatrixGuideOverlay";
import DocumentCapture from "../components/DocumentCapture";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { sealToB64url } from "../crypto/curbsealVerify";
import { analyzeFrame, reconstituteFromGuide } from "../lib/matrixAr";

/**
 * Twin-Matrix scan / claim / verify page.
 * Nested V2 QR lands here. Guide-aligned warp (user lines sticker to yellow square);
 * cam2qr + seal verify in a worker. ?matrixDebug=1 for HUD + warped preview.
 */
export default function TwinMatrixScanner() {
  const { assetCode } = useParams();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const matrixDebug = searchParams.get("matrixDebug") === "1";
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("loading");
  const [cards, setCards] = useState([]);
  const [claiming, setClaiming] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [workerMsg, setWorkerMsg] = useState("Preparing scanner…");
  const [locked, setLocked] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  // Outer matrix geometry from the patch response. Held as state so the guide
  // overlay redraws for the SKU, and mirrored into a ref for the frame loop,
  // which would otherwise close over a stale value.
  const [matrix, setMatrix] = useState(null);
  const [docBusy, setDocBusy] = useState(false);
  const [docResult, setDocResult] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const previewRef = useRef(null);
  const workerRef = useRef(null);
  const rafRef = useRef(0);
  const workerIdle = useRef(true);
  const streamRef = useRef(null);
  const matrixRef = useRef(null);
  const noAnchorFrames = useRef(0);
  const lastDebugUi = useRef(0);

  const stopCamera = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks()?.forEach((t) => t.stop());
    streamRef.current = null;
    workerRef.current?.terminate();
    workerRef.current = null;
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/matrix/${assetCode}`);
        if (cancelled) return;
        setStatus(data);
        if (data.status === "unbound") setPhase("unbound");
        else setPhase("auth");
      } catch (e) {
        if (!cancelled) {
          setError(formatApiError(e));
          setPhase("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assetCode]);

  useEffect(() => {
    if (!user || phase !== "unbound") return;
    (async () => {
      try {
        const { data } = await api.get("/cardholder/cards");
        setCards((data.cards || []).filter((c) => !c.lost_reported && c.is_active !== false));
      } catch {
        setCards([]);
      }
    })();
  }, [user, phase]);

  const claim = async (chId) => {
    setClaiming(true);
    setError("");
    try {
      await api.post(`/cardholder/cards/${chId}/assets/claim`, { asset_code: assetCode });
      setStatus((s) => ({ ...s, status: "bound" }));
      setPhase("auth");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setClaiming(false);
    }
  };

  const drawDebugOverlay = (centers, orient, guideCorners, w, h) => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    overlay.width = w;
    overlay.height = h;
    const ctx = overlay.getContext("2d");
    ctx.clearRect(0, 0, w, h);
    if (guideCorners?.length === 4) {
      ctx.strokeStyle = "#FFCC00";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(guideCorners[0].x, guideCorners[0].y);
      for (let i = 1; i < 4; i++) ctx.lineTo(guideCorners[i].x, guideCorners[i].y);
      ctx.closePath();
      ctx.stroke();
      ctx.setLineDash([]);
    }
    for (const c of centers || []) {
      ctx.strokeStyle = "#00E5FF";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(c.x, c.y, Math.max(6, c.size / 2), 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = "#00E5FF";
      ctx.font = "12px monospace";
      ctx.fillText(`${Math.round(c.size)}`, c.x + 6, c.y - 6);
    }
    if (orient) {
      ctx.strokeStyle = "#16a34a";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(orient.tl.x, orient.tl.y);
      ctx.lineTo(orient.bl.x, orient.bl.y);
      ctx.lineTo(orient.br.x, orient.br.y);
      ctx.lineTo(orient.tr.x, orient.tr.y);
      ctx.closePath();
      ctx.stroke();
      ctx.fillStyle = "#16a34a";
      ctx.fillText("TL", orient.tl.x - 10, orient.tl.y - 8);
      ctx.fillText("BL", orient.bl.x - 10, orient.bl.y + 14);
      ctx.fillText("TR?", orient.tr.x + 4, orient.tr.y - 8);
    }
  };

  const startScan = async () => {
    setError("");
    setPhase("scan");
    setWorkerMsg("Fetching unlock corner…");
    setLocked(false);
    setDebugInfo(null);
    noAnchorFrames.current = 0;

    try {
      const patchRes = await api.get(`/matrix/${assetCode}/missing-modules`);
      const { matrix_kid: kid, matrix_grid: grid, kid_index: kidIndex } = patchRes.data;
      const keyRes = await api.get(`/matrix/keys/${kid}`);
      const publicKeySpki = keyRes.data.public_spki_b64;
      const descriptor = { grid, missingModules: patchRes.data.missing_modules };
      matrixRef.current = descriptor;
      setMatrix(descriptor);

      if (!publicKeySpki) {
        throw new Error("Manufacturer public key missing SPKI — re-register key in admin");
      }

      const worker = new Worker(new URL("../workers/matrixVerify.worker.js", import.meta.url));
      workerRef.current = worker;
      workerIdle.current = true;

      worker.onmessage = async (e) => {
        const msg = e.data;
        if (msg.type === "idle") workerIdle.current = true;
        if (msg.type === "status") setWorkerMsg(msg.message || "Scanning…");
        if (msg.type === "forged") {
          setPhase("forged");
          stopCamera();
        }
        if (msg.type === "error") {
          const m = msg.message || "Worker error";
          if (!/end of buffer/i.test(m) && !/data read/i.test(m)) setWorkerMsg(m);
        }
        if (msg.type === "verified") {
          stopCamera();
          setWorkerMsg("Offline seal OK — confirming with server…");
          try {
            const { data } = await api.post(`/matrix/${assetCode}/verify`, {
              seal_b64url: sealToB64url(new Uint8Array(msg.sealBytes)),
            });
            setReveal(data);
            setPhase("done");
          } catch (err) {
            setError(formatApiError(err));
            setPhase("auth");
          }
        }
      };

      worker.postMessage({ type: "init", publicKeySpki, assetCode, kidIndex, grid });

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      const video = videoRef.current;
      video.srcObject = stream;
      video.setAttribute("playsinline", "true");
      video.muted = true;
      await video.play();
      setWorkerMsg(
        grid === 36
          ? "Fill the yellow square with the Data Matrix — solid L on the left and bottom"
          : "Fill the yellow square with the big matrix — TL/BL on the left marks"
      );

      const tick = () => {
        const canvas = canvasRef.current;
        if (!canvas || !video.videoWidth) {
          rafRef.current = requestAnimationFrame(tick);
          return;
        }
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(video, 0, 0);

        if (workerIdle.current && workerRef.current && matrixRef.current) {
          workerIdle.current = false;
          const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const dispW = canvas.clientWidth;
          const dispH = canvas.clientHeight;
          if (!dispW || !dispH) {
            workerIdle.current = true;
            rafRef.current = requestAnimationFrame(tick);
            return;
          }

          let guide = { patched: null, corners: null, warpOk: false };
          try {
            guide = reconstituteFromGuide(frame, matrixRef.current, dispW, dispH);
          } catch {
            guide = { patched: null, corners: null, warpOk: false };
          }

          // Finder HUD only in debug — not required for unlock
          let analysis = { centers: [], orient: null, thr: 0 };
          if (matrixDebug) {
            try {
              analysis = analyzeFrame(frame, { grid: matrixRef.current.grid });
            } catch {
              /* ignore */
            }
            drawDebugOverlay(
              analysis.centers,
              analysis.orient,
              guide.corners,
              canvas.width,
              canvas.height
            );
            const now = Date.now();
            if (now - lastDebugUi.current > 200) {
              lastDebugUi.current = now;
              setDebugInfo({
                finders: analysis.centers?.length || 0,
                thr: guide.thr ?? Math.round(analysis.thr || 0),
                locked: !!guide.patched,
                warpOk: !!guide.warpOk,
                looksLikeQr: guide.looksLikeQr === true,
                zoom: guide.zoom != null ? guide.zoom.toFixed(2) : "—",
                anchorMatch:
                  guide.anchorMatch != null ? Math.round(guide.anchorMatch * 100) : null,
              });
              if (guide.patched && previewRef.current) {
                const p = previewRef.current;
                p.width = guide.patched.width;
                p.height = guide.patched.height;
                p.getContext("2d").putImageData(guide.patched, 0, 0);
              }
            }
          }

          if (!guide.patched) {
            setLocked(false);
            noAnchorFrames.current += 1;
            if (noAnchorFrames.current === 12 || noAnchorFrames.current % 40 === 0) {
              setWorkerMsg("Align the big sticker to the yellow square — hold flat, good light");
            }
            workerIdle.current = true;
          } else {
            noAnchorFrames.current = 0;
            setLocked(true);
            setWorkerMsg(
              guide.looksLikeQr
                ? "Aligned — verifying seal…"
                : `Hold steady — ${Math.round((guide.anchorMatch || 0) * 100)}% locked on`
            );
            workerRef.current.postMessage(
              { type: "frame", imageData: guide.patched, patched: true },
              [guide.patched.data.buffer]
            );
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      setError(formatApiError(e) || String(e?.message || e));
      setPhase("auth");
      stopCamera();
    }
  };

  if (authLoading || phase === "loading") {
    return (
      <div>
        <Nav />
        <div className="px-4 py-20 text-center label-tag">Loading…</div>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div>
        <Nav />
        <div className="max-w-md mx-auto px-4 py-12">
          <h1 className="font-display font-black text-2xl">Twin-Matrix</h1>
          <p className="mt-2 text-red-600 label-tag">{error || "Not found"}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Nav />
      <div className="max-w-md mx-auto px-4 py-8">
        <div className="label-tag text-neutral-500">
          {status?.kind === "document" ? "Document seal" : status?.kind === "wine" ? "Bottle seal" : "Twin-Matrix"}
        </div>
        <h1 className="font-display font-black text-3xl mt-1">{assetCode}</h1>
        {status && (
          <p className="text-sm text-neutral-600 mt-1">
            Status: <strong>{status.status}</strong>
          </p>
        )}
        {error && <p className="mt-3 label-tag text-red-600">{error}</p>}

        {phase === "unbound" && (
          <div className="mt-6 border-2 border-black bg-white p-4" data-testid="matrix-claim">
            <p className="font-display font-bold">Unclaimed sticker</p>
            <p className="text-sm text-neutral-700 mt-1">Bind it to your Curb Crew card, then unlock with the camera.</p>
            {!user ? (
              <Link
                to={`/cardholder/login?next=${encodeURIComponent(pathname)}`}
                className="mt-4 inline-block label-tag px-4 py-3 border-2 border-black bg-[color:var(--cc-yellow)] brutal-btn"
              >
                Log in to claim →
              </Link>
            ) : (
              <ul className="mt-4 space-y-2">
                {cards.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      disabled={claiming}
                      onClick={() => claim(c.id)}
                      className="w-full text-left border-2 border-black px-3 py-2 brutal-btn bg-white"
                    >
                      <div className="font-mono-cc text-sm">{c.code}</div>
                      <div className="label-tag text-xs">{c.full_name || "Claim →"}</div>
                    </button>
                  </li>
                ))}
                {cards.length === 0 && (
                  <p className="text-sm">No active cards — activate one first.</p>
                )}
              </ul>
            )}
          </div>
        )}

        {(phase === "auth" || (phase === "unbound" && status?.status === "bound")) && user && status?.status === "bound" && (
          <div className="mt-6 border-2 border-black bg-white p-4">
            <p className="font-display font-bold">Verify physical seal</p>
            <p className="text-sm text-neutral-700 mt-1">
              Fill the yellow square with the big (broken) matrix — TL/BL marks on the left. We warp that box, paint the void, and verify offline.
            </p>
            <button
              type="button"
              onClick={startScan}
              className="mt-4 w-full label-tag px-4 py-3 bg-black text-white brutal-btn"
              data-testid="matrix-start-scan"
            >
              Start camera unlock
            </button>
            {!matrixDebug && (
              <p className="text-xs text-neutral-500 mt-3">
                Dev: <code>?matrixDebug=1</code> shows warp preview + optional finder dots.
              </p>
            )}
          </div>
        )}

        {phase === "auth" && !user && status?.status === "bound" && (
          <div className="mt-6 border-2 border-black p-4">
            <p className="text-sm">
              {status?.kind === "document"
                ? "This document is sealed. Log in to verify it."
                : status?.kind === "wine"
                  ? "Log in to scan the big code and confirm it matches this sticker."
                  : "This sticker is claimed. Log in as the owner to unlock."}
            </p>
            <Link
              to={`/cardholder/login?next=${encodeURIComponent(pathname)}`}
              className="mt-3 inline-block label-tag px-4 py-3 border-2 border-black bg-[color:var(--cc-yellow)] brutal-btn"
            >
              Log in →
            </Link>
          </div>
        )}

        {phase === "scan" && (
          <div className="mt-6">
            <p className="label-tag mb-2">{workerMsg}</p>
            <div className="relative border-2 border-black bg-black overflow-hidden" data-testid="matrix-viewfinder-wrap">
              <video ref={videoRef} className="hidden" playsInline muted />
              <canvas ref={canvasRef} className="block w-full h-auto" data-testid="matrix-viewfinder" />
              {matrixDebug && (
                <canvas ref={overlayRef} className="absolute inset-0 h-full w-full pointer-events-none" />
              )}
              {matrix && (
                <MatrixGuideOverlay
                  locked={locked}
                  grid={matrix.grid}
                  missingModules={matrix.missingModules}
                  symbol={matrix.grid === 36 ? "datamatrix" : "qr"}
                />
              )}
            </div>
            {matrixDebug && (
              <div className="mt-3 border-2 border-dashed border-neutral-400 bg-neutral-50 p-3 text-xs font-mono space-y-2" data-testid="matrix-debug">
                <div>
                  debug · guide={String(!!debugInfo?.warpOk)} anchors=
                  {debugInfo?.anchorMatch != null ? `${debugInfo.anchorMatch}%` : "—"} zoom=
                  {debugInfo?.zoom ?? "—"} finders={debugInfo?.finders ?? "—"} thr=
                  {debugInfo?.thr ?? "—"} locked={String(!!debugInfo?.locked)}
                </div>
                <canvas ref={previewRef} className="max-w-full border border-black bg-white" style={{ imageRendering: "pixelated", width: 164, height: 164 }} />
              </div>
            )}
            <button type="button" onClick={() => { stopCamera(); setPhase("auth"); }} className="mt-3 label-tag underline">
              Cancel
            </button>
          </div>
        )}

        {phase === "forged" && (
          <div className="mt-6 border-2 border-red-700 bg-red-50 p-4">
            <p className="font-display font-bold text-red-800">Seal check failed</p>
            <p className="text-sm mt-1">QR decoded but signature did not verify. Treat as counterfeit / tampered.</p>
            <button type="button" className="mt-3 label-tag underline" onClick={() => setPhase("auth")}>
              Try again
            </button>
          </div>
        )}

        {phase === "docscan" && reveal?.document && (
          <DocumentCapture
            busy={docBusy}
            error={error}
            onCancel={() => setPhase("done")}
            onCapture={async (file) => {
              setDocBusy(true);
              setError("");
              const body = new FormData();
              body.append("image", file);
              body.append("verification_token", reveal.verification_token);
              try {
                const { data } = await api.post(
                  `/documents/by-asset/${reveal.asset_code}/verify-scan`,
                  body
                );
                setDocResult(data);
                setPhase("docresult");
              } catch (err) {
                setError(formatApiError(err));
              } finally {
                setDocBusy(false);
              }
            }}
          />
        )}

        {phase === "docresult" && docResult && (
          <div
            className={`mt-6 border-2 p-4 ${
              docResult.is_match ? "border-black bg-[color:var(--cc-yellow)]" : "border-red-700 bg-red-50"
            }`}
            data-testid="document-verify-result"
          >
            <p className="font-display font-bold">
              {docResult.is_match ? "Details match" : "Details do not match — possible tampering"}
            </p>
            <p className="text-sm mt-2">
              On file: {docResult.expected_first_name} {docResult.expected_surname}
            </p>
            <p className="text-sm">
              On this photo: {docResult.found_first_name || "—"} {docResult.found_surname || ""}
            </p>
            <button type="button" className="mt-3 label-tag underline" onClick={() => setPhase("done")}>
              Back
            </button>
          </div>
        )}

        {phase === "done" && reveal && (
          <div className="mt-6 border-2 border-black bg-[color:var(--cc-yellow)] p-4" data-testid="matrix-reveal">
            <p className="label-tag">Verified</p>
            <h2 className="font-display font-black text-2xl mt-1">
              {status?.kind === "wine" ? "Small QR matches the big code" : (reveal.label || "Untitled")}
            </h2>
            {(reveal.item || reveal.make || reveal.model) && (
              <p className="text-sm mt-2">
                {[reveal.item, reveal.make, reveal.model].filter(Boolean).join(" · ")}
              </p>
            )}
            {reveal.notes && <p className="text-sm mt-2 text-neutral-800">{reveal.notes}</p>}
            <p className="label-tag text-xs mt-3">Serial {reveal.matrix_serial}</p>
            {reveal.document && (
              <div className="mt-4 border-2 border-black bg-white p-3">
                <p className="font-display font-bold">Verify the document?</p>
                <p className="text-sm mt-1">
                  Photograph the page. We read the name again and compare it with{" "}
                  {reveal.document.holder_first_name} {reveal.document.holder_surname}.
                </p>
                <button
                  type="button"
                  className="mt-3 label-tag px-3 py-2 bg-black text-white brutal-btn"
                  onClick={() => {
                    setError("");
                    setPhase("docscan");
                  }}
                >
                  Yes, scan the page
                </button>
              </div>
            )}
            <button
              type="button"
              className="mt-4 label-tag px-3 py-2 border-2 border-black bg-white brutal-btn"
              onClick={() => nav(status?.kind === "wine" ? `/wine/${status.asset_code}` : "/my-cards")}
            >
              {status?.kind === "wine" ? "Back to bottle" : "My cards →"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
